import { BreakpointObserver } from "@angular/cdk/layout";
import { Injectable } from "@angular/core";
import { RouterActions } from "@app/root-store";
import { Store } from "@ngrx/store";
import { environment } from "environments/environment";
import { getApp, initializeApp } from "firebase/app";
import {
    AuthProvider,
    CompleteFn,
    ErrorFn,
    User as FirebaseUser,
    IdTokenResult,
    NextOrObserver,
    browserSessionPersistence,
    confirmPasswordReset,
    createUserWithEmailAndPassword,
    fetchSignInMethodsForEmail,
    getAuth,
    onAuthStateChanged,
    sendPasswordResetEmail,
    setPersistence,
    signInWithEmailAndPassword,
    signInWithRedirect,
    signOut,
    verifyPasswordResetCode,
} from "firebase/auth";
import { Observable, map, switchMap, take } from "rxjs";
import { AuthState } from "../store";

@Injectable({
    providedIn: "root",
})
export class FirebaseAuthService {
    get authInstance() {
        return getAuth(getApp());
    }

    constructor(private readonly store: Store<AuthState>, private readonly breakpointObserver$: BreakpointObserver) {
        initializeApp(environment.firebaseConfig);
    }

    setBrowserSessionPersistence() {
        setPersistence(this.authInstance, browserSessionPersistence)
            .then(() => {
                console.log("Persistence set successfully. ");
                // Now, you can sign in users with this persistence setting.
            })
            .catch(error => {
                console.error("Error setting persistence:", error);
            });
    }

    getFirebaseUser() {
        return getAuth().currentUser;
    }

    onAuthStateChanged(obs$: NextOrObserver<FirebaseUser>, error?: ErrorFn, complete?: CompleteFn) {
        return onAuthStateChanged(this.authInstance, obs$, error, complete);
    }

    signInWithRedirect(provider: AuthProvider) {
        return signInWithRedirect(this.authInstance, provider);
    }

    signInWithEmailAndPassword(email: string, password: string) {
        return signInWithEmailAndPassword(this.authInstance, email, password);
    }

    signOut() {
        return signOut(this.authInstance);
    }

    fetchSignInMethodsForEmail(email: string) {
        return fetchSignInMethodsForEmail(this.authInstance, email);
    }

    sendPasswordResetEmail(email: string) {
        // This sends the url that will be set as continueUrl in url set in password reset email.
        return sendPasswordResetEmail(this.authInstance, email, {
            url: `${window.location.protocol}//${window.location.host}`,
        });
    }

    confirmPasswordReset(oobCode: string, password: string, continueUrl: string) {
        return this.breakpointObserver$.observe(["(max-width: 959px)"]).pipe(
            take(1),
            map(mobileQuery => mobileQuery.matches),
            switchMap(isMobile =>
                confirmPasswordReset(this.authInstance, oobCode, password).then(() => {
                    if (!continueUrl) {
                        // continueUrl only gets set from fusion pro, so if there is no continueUrl, we know it came from the mobile app.
                        if (isMobile) {
                            // redirect back to mobile app
                            window.location.href = "clarehome://login";
                        } else {
                            // redirect to new landing page. Scenario: forgot password sent from mobile, opened on desktop browser.
                            this.store.dispatch(RouterActions.routerGo({ path: ["login/mobile-forgot-password"] }));
                        }
                    } else {
                        // continueUrl is based on dev environment. redirect to login page
                        document.location.href = `${continueUrl}/login`;
                    }
                })
            )
        );
    }

    verifyPasswordResetCode(oobCode: string) {
        return verifyPasswordResetCode(this.authInstance, oobCode);
    }

    createUserWithEmailAndPassword(email: string, password: string) {
        return createUserWithEmailAndPassword(this.authInstance, email, password);
    }

    // The rest of the Firebase API stubbed out
    readonly app = null;
    applyActionCode = null;
    checkActionCode = null;
    credential = null;
    isSignInWithEmailLink = null;
    languageCode = null;
    settings = null;
    onIdTokenChanged = null;
    sendSignInLinkToEmail = null;
    setPersistence = null;
    getRedirectResult = null;
    currentUser = null;
    signInAndRetrieveDataWithCredential = null;
    signInAnonymously = null;
    signInWithCredential = null;
    signInWithCustomToken = null;
    signInWithPhoneNumber = null;
    signInWithEmailLink = null;
    signInWithPopup = null;
    tenantId = null;
    useEmulator = null;
    readonly updateCurrentUser = null;
    readonly useDeviceLanguage = null;
    readonly authState: Observable<FirebaseUser | null>;
    readonly idToken: Observable<string | null>;
    readonly user: Observable<FirebaseUser | null>;
    readonly idTokenResult: Observable<IdTokenResult | null>;
}
